import { Button, ButtonLink } from '@/components/ui/button';
import { useEffect, useState  } from 'react';
import type {FormEvent} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '@/components/layout/page';
import { DevNote } from '@/components/ui/dev-note';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';
import { paths } from '@/lib/paths';

/**
 * Désinscription de la newsletter — `POST /newsletter/unsubscribe`.
 *
 * Cette page couvre le cas d'une désinscription **manuelle**. Chaque email
 * envoyé porte par ailleurs un lien de désinscription en un clic, géré
 * entièrement par la plateforme : il ne demande aucune intégration de votre
 * part.
 *
 * L'adresse peut être pré-remplie par la query string (`?email=…`), ce qui
 * permet de pointer un lien vers cette page depuis n'importe où sans obliger
 * le visiteur à ressaisir son adresse.
 *
 * La désinscription arrête **tout** : campagnes à venir comme séquences en
 * cours.
 */
export default function UnsubscribePage() {
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState(searchParams.get('email') ?? '');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    // Si l'adresse change dans l'URL (navigation interne), on la reprend.
    useEffect(() => {
        const fromUrl = searchParams.get('email');

        if (fromUrl) {
            setEmail(fromUrl);
        }
    }, [searchParams]);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await dashlite.unsubscribeNewsletter({ email: email.trim() });
            setDone(true);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Se désinscrire">
            <PageHeader
                eyebrow="Newsletter"
                title="Se désinscrire"
                description="Nous arrêtons immédiatement tous les envois vers cette adresse."
            />

            <Section>
                <Container className="max-w-xl space-y-6">
                    {done ? (
                        <>
                            <Alert tone="positive" title="C'est fait">
                                Cette adresse ne recevra plus aucun message : ni campagne, ni séquence en cours. Si
                                vous changez d'avis, vous pourrez vous réinscrire à tout moment.
                            </Alert>

                            <div className="flex gap-3">
                                <ButtonLink to={paths.home} variant="secondary">
                                    Retour à l'accueil
                                </ButtonLink>
                                <ButtonLink to={paths.newsletter} variant="ghost">
                                    Me réinscrire
                                </ButtonLink>
                            </div>
                        </>
                    ) : (
                        <Card>
                            <CardBody>
                                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                                    <Input
                                        label="Adresse email"
                                        type="email"
                                        required
                                        autoFocus
                                        autoComplete="email"
                                        value={email}
                                        error={fields.email}
                                        onChange={(event) => setEmail(event.target.value)}
                                    />

                                    {error !== null && Object.keys(fields).length === 0 && (
                                        <Alert tone="critical" title={presentError(error).message}>
                                            {presentError(error).hint}
                                        </Alert>
                                    )}

                                    <Button type="submit" size="lg" loading={submitting}>
                                        Me désinscrire
                                    </Button>
                                </form>
                            </CardBody>
                        </Card>
                    )}

                    <DevNote title="Ce que fait cet écran" endpoints={['POST /newsletter/unsubscribe']}>
                        <p>
                            La désinscription arrête tout : campagnes à venir comme séquences en cours. Elle est
                            respectée par la suite — y compris par l'inscription automatique qui accompagne un achat.
                        </p>
                        <p>
                            Comme pour l'inscription, la réponse ne dit pas si l'adresse figurait dans la liste.
                        </p>
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
